import React from 'react';
import {
  Box,
  Heading,
  Text,
  Stack,
  List,
  ListItem,
  ListIcon,
  Button,
  Divider,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FiInfo, FiUser, FiFile, FiAward, FiCheckCircle, FiArrowLeft } from 'react-icons/fi';

/**
 * Introduction Form Component
 * Displays information about the application process steps
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onStart - Function to execute when the Start button is clicked
 */
const IntroductionForm = ({ onStart }) => {
  // Steps information
  const steps = [
    {
      id: 1,
      title: 'المعلومات الشخصية',
      icon: FiUser,
      description: 'في هذه الخطوة ستقوم بإدخال المعلومات الشخصية الأساسية مثل الاسم، رقم الهوية، معلومات الاتصال، والعنوان.',
      fields: [
        'الاسم الكامل', 
        'رقم الهوية', 
        'تاريخ الميلاد', 
        'الجنس',
        'رقم الجوال', 
        'البريد الإلكتروني', 
        'معلومات العمل',
        'العنوان الوطني'
      ]
    },
    {
      id: 2,
      title: 'المستندات',
      icon: FiFile,
      description: 'في هذه الخطوة ستقوم بتحميل المستندات المطلوبة مثل السيرة الذاتية، الصورة الشخصية، والمسح الأمني.',
      fields: [
        'السيرة الذاتية',
        'الصورة الشخصية',
        'المسح الأمني',
        'أي مستندات داعمة أخرى'
      ]
    },
    {
      id: 3,
      title: 'الترشيح',
      icon: FiAward,
      description: 'في هذه الخطوة ستقوم باختيار الفرع والمركز وتقديم معلومات عن رؤيتك ومساهماتك المجتمعية.',
      fields: [
        'اختيار الفرع والمركز',
        'معرفتك بالجمعية',
        'المساهمة في المسؤولية الاجتماعية',
        'المساهمة في برامج رؤية 2030',
        'أفكار لزيادة دخل المركز'
      ]
    },
    {
      id: 4,
      title: 'المراجعة',
      icon: FiCheckCircle,
      description: 'في هذه الخطوة ستقوم بمراجعة جميع المعلومات التي أدخلتها والموافقة على الشروط قبل تقديم الطلب النهائي.',
      fields: [
        'مراجعة المعلومات الشخصية',
        'مراجعة المستندات المرفقة',
        'مراجعة معلومات الترشيح',
        'الإقرار و الموافقة على الشروط والأحكام',
        'تقديم الطلب النهائي'
      ]
    }
  ];

  return (
    <Box>
      {/* Introduction Header */}
      <Box textAlign="center" mb={8}>
        <Heading as="h2" size="xl" mb={4}>
          نظام التقديم لمجالس مراكز الأحياء
        </Heading>
        <Text fontSize="lg" color="gray.600">
          مرحباً بك في نظام التقديم لمجالس مراكز الأحياء. يرجى قراءة المعلومات التالية قبل البدء في ملء النموذج.
        </Text>
      </Box>

      {/* Important Information */}
      <Box 
        p={5} 
        borderRadius="md" 
        bg="blue.50" 
        mb={8}
      >
        <Flex alignItems="center" mb={2}>
          <Icon as={FiInfo} color="blue.500" boxSize={6} mr={2} />
          <Heading as="h3" size="md" color="blue.700">
            معلومات هامة
          </Heading>
        </Flex>
        <Text>
          • يرجى تجهيز جميع المستندات المطلوبة قبل البدء في تعبئة النموذج.
        </Text>
        <Text>
          • يمكنك حفظ تقدمك في أي وقت والعودة لاستكمال الطلب لاحقاً.
        </Text>
        <Text>
          • تأكد من إدخال معلومات صحيحة ودقيقة، حيث سيتم التحقق منها.
        </Text>
        <Text>
          • يمكنك تحميل <a href="https://majalisna-files-2025.s3.me-south-1.amazonaws.com/public-folder/%D9%86%D9%85%D9%88%D8%B0%D8%AC+%D8%A7%D9%84%D9%85%D8%B3%D8%AD+%D8%A7%D9%84%D8%A3%D9%85%D9%86%D9%8A+1.docx" target="_blank" rel="noreferrer" style={{color: 'blue', textDecoration: 'underline'}}>نموذج المسح الأمني</a> وتعبئته قبل التقديم.
        </Text>
        <Text>
          • يمكنك الاطلاع على <a href="/neighborhood-council-regulations" target="_blank" rel="noreferrer" style={{color: 'blue', textDecoration: 'underline'}}>لائحة التشكيل</a> للحصول على كافة التفاصيل.
        </Text>
      </Box>

      {/* Steps Explanation */}
      <Heading as="h3" size="md" mb={4}>
        خطوات التقديم
      </Heading>
      
      <Stack spacing={6} mb={8}>
        {steps.map((step) => (
          <Box key={step.id} p={5} borderWidth="1px" borderRadius="md" borderColor="gray.200">
            <Flex alignItems="center" mb={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="full"
                bg="primary.100"
                color="primary.700"
                justify="center"
                align="center"
                mr={3}
              >
                <Icon as={step.icon} boxSize={5} />
              </Flex>
              <Heading as="h4" size="md">
                {step.title}
              </Heading>
            </Flex>
            
            <Text mb={3}>{step.description}</Text>
            
            <Text fontWeight="bold" mb={2}>الحقول المطلوبة:</Text>
            <List spacing={1}>
              {step.fields.map((field, index) => (
                <ListItem key={index} display="flex" alignItems="center">
                  <ListIcon as={FiArrowLeft} color="green.500" />
                  <Text>{field}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Stack>
      
      <Divider my={6} />
      
      {/* Start Button */}
      <Flex justify="center">
        <Button
          size="lg"
          colorScheme="primary"
          leftIcon={<FiArrowLeft />}
          onClick={onStart}
          px={8}
        >
          ابدأ التقديم الآن
        </Button>
      </Flex>
    </Box>
  );
};

export default IntroductionForm;
